import type React from 'react';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type { ObjectBoxesController } from '../../ObjectBoxes.controller.ts';
import type { ICustomCommissurePointsBoxController } from '@/components/boxes/objectBoxes/interfaces.ts';
import type { IObjectViewModel } from '@/engine/app/interfaces.ts';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { TYPES } from '@/engine/ioc';
import { useInjection } from 'inversify-react';
import { useTheme } from '@mui/material/styles';
import { EyeCheckboxButton, PenCheckboxButton } from '../../../CheckboxButton.tsx';
import { StyledTableCell, UpdatableResetIconButton } from '../../../BoxTools.tsx';
import { useHighlight } from '../../../hooks/useHighlight.ts';
import { useSelect } from '../../../hooks/useSelect.ts';
import { getConditionalStyles } from '../../../styles.ts';
import { EditableCellTextField } from '@/components/common/EditableCellTextField.tsx';
import { IconButton, Tooltip } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { observer } from 'mobx-react';

export interface LabelBoxProps {
  objects: Array<IObjectViewModel>;
  boxController: ICustomCommissurePointsBoxController;
}

export const LabelBox: React.FC<LabelBoxProps> = observer((props: LabelBoxProps) => {
  const theme = useTheme();

  const mainController: ObjectBoxesController = useInjection<ObjectBoxesController>(TYPES.IObjectBoxesController);
  const viewsService: ViewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const objectVMs = viewsService.currentView?.getObjects();

  const { isHighlighted } = useHighlight(objectVMs);
  const { isSelected } = useSelect(objectVMs);

  return (
    <TableContainer component={Paper}>
      <Table aria-label="a dense table" size="small">
        <TableBody>
          {props.objects.map((object: IObjectViewModel, index: number) => {
            const isItemHighlighted: boolean = isHighlighted(object.descriptor);
            const isItemSelected: boolean = isSelected(object.descriptor);

            return (
              <TableRow
                key={object.label + index}
                data-testid={object.descriptor + 'Label'}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:hover button.locate-button': {
                    opacity: 1,
                    color: 'primary.main'
                  },
                  '& button.locate-button': { opacity: 0.1 },
                  ...getConditionalStyles(
                    isItemHighlighted,
                    theme,
                    { paddingLeft: 0.3, paddingTop: 0, paddingBottom: 0 },
                    {
                      paddingRight: 0.5,
                      paddingTop: 0,
                      paddingBottom: 0
                    }
                  )
                }}
                onMouseEnter={(): void => mainController.setObjectHighlighting(object.descriptor, true)}
                onMouseLeave={(): void => mainController.setObjectHighlighting(object.descriptor, false)}
                selected={isItemSelected}
                onClick={(): void => {
                  mainController.setObjectSelection(object.descriptor, !isItemSelected);
                }}
              >
                <TableCell component="th" scope="row" key={'label' + index}>
                  {object.imported ? (
                    <span>{props.boxController.resolveCommissurePointLabel(object.descriptor)}</span>
                  ) : (
                    <EditableCellTextField
                      value={props.boxController.resolveCommissurePointLabel(object.descriptor) ?? object.label}
                      onBlur={(e): void => {
                        props.boxController.setLabel(object.descriptor, e.currentTarget.value);
                      }}
                    />
                  )}
                </TableCell>
                <StyledTableCell className="limited-width light-fill" align={'center'}>
                  {object.imported && (
                    <UpdatableResetIconButton
                      descriptor={object.descriptor}
                      controller={mainController}
                      attachmentNumber={2}
                    />
                  )}
                </StyledTableCell>
                <StyledTableCell
                  align="right"
                  key={'delete' + index}
                  className="limited-width"
                  onClick={(event): void => event.stopPropagation()}
                >
                  <Tooltip
                    title="Delete"
                    placement="left"
                    hidden={object.imported}
                    data-testid={'delete-commissure-point-tooltip'}
                  >
                    <span>
                      <IconButton
                        data-testid={'delete-commissure-point'}
                        size="small"
                        color="primary"
                        onClick={(): void => {
                          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
                          props.boxController.deleteCommissurePoint(object.descriptor);
                        }}
                      >
                        <DeleteOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </StyledTableCell>
                {object.isTunable && (
                  <StyledTableCell align="right" key={'edit' + index} className="limited-width">
                    <PenCheckboxButton
                      onChange={(checked): void => {
                        mainController.setObjectBeingEdited(object.descriptor, checked);
                      }}
                      checked={object.isBeingEdited}
                      key={'beingEditedBtn' + index}
                    />
                  </StyledTableCell>
                )}
                <StyledTableCell
                  align="right"
                  key={'visibility' + index}
                  className="limited-width"
                  onClick={(event): void => event.stopPropagation()}
                >
                  <EyeCheckboxButton
                    onChange={(checked: boolean): void =>
                      mainController.setObjectVisibility(object.descriptor, checked)
                    }
                    checked={object.isVisible}
                  />
                </StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
